import React, { useState } from "react";
import { baseUrl, getAccessToken } from "../../../../serverUrl";

function getDefaultConfig() {
  let token = "";

  try {
    token = getAccessToken() || "";
  } catch (error) {
    token = "";
  }

  return {
    apiBaseUrl: baseUrl,
    bearerToken: token,
    landlordId: "102",
    apartmentId: "",
    companyId: "",
    month: "february",
    year: "2026",
    cutoff: "2026-02-26T00:00",
    generationBackdatedCreatedAt: "",
    replaceExisting: true,
  };
}

async function handleJsonResponse(response) {
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const errorMessage =
      data?.error || data?.detail || `Request failed with status ${response.status}`;
    throw new Error(errorMessage);
  }

  return data;
}

function toDateTimeLocalValue(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const pad = (item) => String(item).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function InputField({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-slate-700">{label}</span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-500"
      />
    </label>
  );
}

function SummaryCard({ label, value, tone = "slate" }) {
  const toneClasses = {
    slate: "border-slate-200 bg-white text-slate-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    rose: "border-rose-200 bg-rose-50 text-rose-900",
    sky: "border-sky-200 bg-sky-50 text-sky-900",
  };

  return (
    <div className={`rounded-2xl border px-4 py-4 ${toneClasses[tone] || toneClasses.slate}`}>
      <p className="text-xs font-semibold uppercase tracking-wide opacity-70">{label}</p>
      <p className="mt-2 text-2xl font-semibold">{value}</p>
    </div>
  );
}

function JsonPanel({ title, data, emptyMessage }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-5 py-4">
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      </div>
      <div className="px-5 py-4">
        {data ? (
          <pre className="max-h-[28rem] overflow-auto rounded-2xl bg-slate-950 p-4 text-xs leading-6 text-slate-100">
            {JSON.stringify(data, null, 2)}
          </pre>
        ) : (
          <p className="text-sm text-slate-500">{emptyMessage}</p>
        )}
      </div>
    </div>
  );
}

function ActionButton({ label, onClick, disabled, tone = "slate" }) {
  const toneStyles = {
    slate: {
      backgroundColor: "#0f172a",
      borderColor: "#0f172a",
      color: "#ffffff",
    },
    sky: {
      backgroundColor: "#075985",
      borderColor: "#075985",
      color: "#ffffff",
    },
    emerald: {
      backgroundColor: "#065f46",
      borderColor: "#065f46",
      color: "#ffffff",
    },
    amber: {
      backgroundColor: "#f59e0b",
      borderColor: "#d97706",
      color: "#111827",
    },
    ghost: {
      backgroundColor: "#ffffff",
      borderColor: "#94a3b8",
      color: "#0f172a",
    },
  };

  const disabledStyle = {
    backgroundColor: "#e5e7eb",
    borderColor: "#cbd5e1",
    color: "#475569",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={disabled ? disabledStyle : toneStyles[tone] || toneStyles.slate}
      className="min-h-[52px] rounded-xl border-2 px-4 py-3 text-sm font-semibold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-100"
    >
      {label}
    </button>
  );
}

function DataTable({ title, rows, columns, emptyMessage }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-5 py-4">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
            {rows.length} rows
          </span>
        </div>
      </div>
      <div className="overflow-x-auto">
        {rows.length ? (
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {rows.map((row, index) => (
                <tr key={`${title}-${index}`} className="align-top">
                  {columns.map((column) => (
                    <td key={column.key} className="whitespace-pre-wrap px-4 py-3 text-sm text-slate-700">
                      {column.render ? column.render(row, index) : row[column.key] || "-"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="px-5 py-6 text-sm text-slate-500">{emptyMessage}</div>
        )}
      </div>
    </div>
  );
}

function ManualReviewModal({
  isOpen,
  row,
  modalState,
  setModalState,
  onClose,
  onSearch,
  onCreateInvoice,
  onBackdateInvoice,
  onUseInvoice,
  onClearSelection,
}) {
  if (!isOpen || !row) {
    return null;
  }

  const availableTemplates = row.candidate_templates || [];
  const selectedTarget = modalState.selectedInvoice;

  return (
    <div className="fixed inset-0 z-[1000] bg-slate-950/45 p-3 md:p-5">
      <div className="mx-auto flex h-full max-w-7xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4 md:px-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Manual Reconciliation</p>
            <h2 className="mt-1 text-xl font-semibold text-slate-900 md:text-2xl">Map KRA invoice {row.csv_invoice_number}</h2>
            <p className="mt-2 text-sm text-slate-600">
              Search existing invoices, inspect current KRA status, or create a missing invoice from a template.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
          >
            Close
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-hidden px-5 py-4 md:px-6">
          <div className="grid h-full gap-5 lg:grid-cols-[1fr,1.35fr]">
            <div className="min-h-0 space-y-4 overflow-y-auto pr-1">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">CSV Row</h3>
                <div className="mt-3 grid gap-2 text-sm text-slate-700">
                  <p><span className="font-semibold">Client:</span> {row.client_name}</p>
                  <p><span className="font-semibold">PIN:</span> {row.client_pin || "-"}</p>
                  <p><span className="font-semibold">Amount:</span> {row.amount}</p>
                  <p><span className="font-semibold">Category:</span> {row.category || "-"}</p>
                  <p><span className="font-semibold">Reason:</span> {row.reason}</p>
                  <p><span className="font-semibold">Created:</span> {row.created_at || row.csv_created_at}</p>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Find Or Create Invoice</h3>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                    {modalState.searchResults.length} results
                  </span>
                </div>

                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <InputField
                    label="Search"
                    value={modalState.searchQuery}
                    onChange={(event) => setModalState((current) => ({ ...current, searchQuery: event.target.value }))}
                    placeholder="Client, house, invoice no"
                  />
                  <label className="block">
                    <span className="mb-2 block text-sm font-medium text-slate-700">Template Invoice</span>
                    <select
                      value={modalState.templateInvoiceId}
                      onChange={(event) => setModalState((current) => ({ ...current, templateInvoiceId: event.target.value }))}
                      className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900"
                    >
                      <option value="">Select template</option>
                      {availableTemplates.map((template) => (
                        <option key={template.invoice_id} value={template.invoice_id}>
                          {`${template.invoice_id} | ${template.category} | ${template.house_number || "-"} | ${template.user_name || "-"}`}
                        </option>
                      ))}
                    </select>
                  </label>
                  <InputField
                    label="Target Month"
                    value={modalState.searchMonth}
                    onChange={(event) => setModalState((current) => ({ ...current, searchMonth: event.target.value }))}
                    placeholder="february"
                  />
                  <InputField
                    label="Target Year"
                    value={modalState.searchYear}
                    onChange={(event) => setModalState((current) => ({ ...current, searchYear: event.target.value }))}
                    placeholder="2026"
                  />
                  <InputField
                    label="TenantInvoice Created At"
                    value={modalState.backdatedCreatedAt}
                    onChange={(event) => setModalState((current) => ({ ...current, backdatedCreatedAt: event.target.value }))}
                    placeholder="2026-02-27T09:00"
                    type="datetime-local"
                  />
                </div>

                <div className="mt-3 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
                  If you set a value here, recovery-created invoices will use it as the
                  `TenantInvoice.created_at` timestamp, and you can also backdate any selected
                  existing invoice to match the recovery timeline.
                </div>

                <div className="mt-4 flex flex-wrap gap-3">
                  <ActionButton label={modalState.isSearching ? "Searching..." : "Search Invoices"} onClick={onSearch} disabled={modalState.isSearching} tone="emerald" />
                  <ActionButton label={modalState.isCreating ? "Creating Invoice..." : "Create Missing Invoice"} onClick={onCreateInvoice} disabled={modalState.isCreating || !modalState.templateInvoiceId} tone="amber" />
                  <ActionButton
                    label={modalState.isBackdating ? "Backdating Invoice..." : "Backdate Selected Invoice"}
                    onClick={onBackdateInvoice}
                    disabled={modalState.isBackdating || !selectedTarget || !modalState.backdatedCreatedAt}
                    tone="ghost"
                  />
                </div>

                {modalState.error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">{modalState.error}</div> : null}
                {modalState.message ? <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{modalState.message}</div> : null}
              </div>

              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold">Selected Invoice</p>
                    <p className="mt-2">
                      {selectedTarget
                        ? `${selectedTarget.invoice_id} | ${selectedTarget.house_number || "-"} | ${selectedTarget.user_name || "-"} | ${selectedTarget.month} ${selectedTarget.year}`
                        : "No invoice selected yet."}
                    </p>
                    <p className="mt-2">Created At: {selectedTarget?.created_at || "Unknown"}</p>
                    <p className="mt-2">Existing KRA: {selectedTarget?.existing_kra_invoice?.kra_invoice_number || "None"}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <ActionButton
                      label="Clear Selection"
                      onClick={onClearSelection}
                      disabled={!selectedTarget}
                      tone="ghost"
                    />
                    <ActionButton
                      label="Use Selected And Close"
                      onClick={() => onUseInvoice(selectedTarget, true)}
                      disabled={!selectedTarget}
                      tone="sky"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="min-h-0 overflow-hidden rounded-2xl border border-slate-200 bg-white">
              <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-4 py-4">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Search Results</h3>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                  {modalState.searchResults.length} results
                </span>
              </div>
              <div className="max-h-full overflow-y-auto px-4 py-4">
                <div className="space-y-3">
                  {modalState.searchResults.length ? modalState.searchResults.map((invoice) => {
                    const isSelected = selectedTarget?.invoice_id === invoice.invoice_id;
                    return (
                      <div
                        key={`${invoice.invoice_id}-${invoice.month}-${invoice.year}`}
                        className={`rounded-2xl border p-4 ${isSelected ? "border-sky-500 bg-sky-50" : "border-slate-200 bg-white"}`}
                      >
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                          <div className="space-y-1 text-sm text-slate-700">
                            <p className="font-semibold text-slate-900">{invoice.invoice_id} | {invoice.house_number || "-"} | {invoice.user_name || "-"}</p>
                            <p>{invoice.month} {invoice.year} | {invoice.invoice_period} | Amount {invoice.amount}</p>
                            <p>Category {invoice.category || row.category || "-"}</p>
                            <p>Created At: {invoice.created_at || "Unknown"}</p>
                            <p>Existing KRA: {invoice.existing_kra_invoice?.kra_invoice_number || "None"}</p>
                            {invoice.invoice_number ? <p>Invoice Number: {invoice.invoice_number}</p> : null}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <ActionButton label={isSelected ? "Selected" : "Choose"} onClick={() => onUseInvoice(invoice, false)} disabled={false} tone="ghost" />
                            <ActionButton label="Choose And Close" onClick={() => onUseInvoice(invoice, true)} disabled={false} tone="sky" />
                          </div>
                        </div>
                      </div>
                    );
                  }) : (
                    <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-sm text-slate-500">
                      Search results will appear here. If no invoice exists for the target month, create one from a template.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function InvoiceRecoveryPage() {
  const [config, setConfig] = useState(() => getDefaultConfig());
  const [csvFile, setCsvFile] = useState(null);
  const [activityError, setActivityError] = useState("");
  const [landlordApartments, setLandlordApartments] = useState([]);
  const [isLoadingApartments, setIsLoadingApartments] = useState(false);
  const [isGeneratingB, setIsGeneratingB] = useState(false);
  const [isGeneratingC, setIsGeneratingC] = useState(false);
  const [isGeneratingBoth, setIsGeneratingBoth] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [generationResult, setGenerationResult] = useState(null);
  const [previewResult, setPreviewResult] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [selectedManualTargets, setSelectedManualTargets] = useState({});
  const [manualModalState, setManualModalState] = useState({
    isOpen: false,
    rowIndex: null,
    row: null,
    searchQuery: "",
    searchMonth: "february",
    searchYear: "2026",
    templateInvoiceId: "",
    searchResults: [],
    isSearching: false,
    isCreating: false,
    isBackdating: false,
    backdatedCreatedAt: "",
    error: "",
    message: "",
    selectedInvoice: null,
  });

  const updateField = (field) => (event) => {
    const nextValue = field === "replaceExisting" ? event.target.checked : event.target.value;

    setConfig((current) => ({
      ...current,
      [field]: nextValue,
    }));
  };

  const getToken = () => config.bearerToken || getAccessToken() || "";
  const useLandlordScope = Boolean(config.landlordId);
  const scopeLabel = useLandlordScope
    ? `Landlord ${config.landlordId}`
    : config.apartmentId
      ? `Apartment ${config.apartmentId}`
      : "No scope selected";

  const ensureApiAndToken = () => {
    if (!config.apiBaseUrl) {
      throw new Error("API base URL is required.");
    }
    if (!getToken()) {
      throw new Error("Bearer token is required.");
    }
  };

  const ensureScope = () => {
    if (!config.landlordId && !config.apartmentId) {
      throw new Error("Enter either a landlord ID or an apartment ID.");
    }
  };

  const ensureGenerationInputs = () => {
    ensureApiAndToken();
    ensureScope();

    if (!config.companyId) {
      throw new Error("Company ID is required for invoice generation.");
    }
    if (!config.month || !config.year) {
      throw new Error("Target month and year are required.");
    }
  };

  const buildJsonHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${getToken()}`,
  });

  const fetchLandlordApartments = async (forceRefresh = false) => {
    ensureApiAndToken();

    if (!config.landlordId) {
      throw new Error("Landlord ID is required to load apartments.");
    }

    if (!forceRefresh && landlordApartments.length) {
      return landlordApartments;
    }

    setIsLoadingApartments(true);

    try {
      const response = await fetch(
        `${config.apiBaseUrl}/landlord/landlord-apartment_name/?landlord_id=${config.landlordId}`,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );

      const data = await handleJsonResponse(response);
      const apartments = Array.isArray(data?.results) ? data.results : [];
      setLandlordApartments(apartments);
      return apartments;
    } finally {
      setIsLoadingApartments(false);
    }
  };

  const resolveTargetApartments = async () => {
    ensureScope();

    if (config.landlordId) {
      const apartments = await fetchLandlordApartments(true);
      if (!apartments.length) {
        throw new Error(`No apartments were found for landlord ${config.landlordId}.`);
      }
      return apartments.map((apartment) => ({
        id: apartment.id,
        name: apartment.name,
      }));
    }

    return [
      {
        id: config.apartmentId,
        name: `Apartment ${config.apartmentId}`,
      },
    ];
  };

  const buildGeneratePayload = (category, apartmentId) => {
    const payload = {
      apartment_id: apartmentId,
      company_id: config.companyId,
      category_type: "monthly",
      generate_etims_invoice: false,
      send_sms: false,
      selected_month: config.month,
      selected_year: Number(config.year),
    };

    if (config.generationBackdatedCreatedAt) {
      payload.backdated_created_at = config.generationBackdatedCreatedAt;
    }

    if (category === "C") {
      payload.category_type_kra = "nonkra";
    }

    return payload;
  };

  const runCategoryGeneration = async (category, apartments) => {
    const results = [];

    for (const apartment of apartments) {
      try {
        const response = await fetch(`${config.apiBaseUrl}/tenant/generate_kra_etims_invoice/`, {
          method: "POST",
          headers: buildJsonHeaders(),
          body: JSON.stringify(buildGeneratePayload(category, apartment.id)),
        });

        const data = await handleJsonResponse(response);
        results.push({
          apartment_id: apartment.id,
          apartment_name: apartment.name,
          status: "success",
          response: data,
        });
      } catch (error) {
        results.push({
          apartment_id: apartment.id,
          apartment_name: apartment.name,
          status: "error",
          error: error.message || "Generation failed.",
        });
      }
    }

    return {
      scope: scopeLabel,
      category,
      apartment_count: apartments.length,
      success_count: results.filter((item) => item.status === "success").length,
      failed_count: results.filter((item) => item.status === "error").length,
      results,
    };
  };

  const generateInvoices = async (category) => {
    try {
      ensureGenerationInputs();
      setActivityError("");
      setApplyResult(null);

      const apartments = await resolveTargetApartments();
      const confirmed = window.confirm(
        `Generate ${config.month} ${config.year} category ${category} invoices without KRA and without SMS for ${apartments.length} apartment(s) under ${scopeLabel}?`
      );

      if (!confirmed) {
        return;
      }

      if (category === "B") {
        setIsGeneratingB(true);
      } else {
        setIsGeneratingC(true);
      }

      const result = await runCategoryGeneration(category, apartments);
      setGenerationResult((current) => ({
        ...(current || {}),
        [category]: result,
      }));
    } catch (error) {
      setActivityError(error.message || `Failed to generate category ${category} invoices.`);
    } finally {
      if (category === "B") {
        setIsGeneratingB(false);
      } else {
        setIsGeneratingC(false);
      }
    }
  };

  const generateBoth = async () => {
    try {
      ensureGenerationInputs();
      setActivityError("");
      setApplyResult(null);
      setIsGeneratingBoth(true);

      const apartments = await resolveTargetApartments();
      const confirmed = window.confirm(
        `Generate both category B and category C invoices without KRA and without SMS for ${apartments.length} apartment(s) under ${scopeLabel}?`
      );

      if (!confirmed) {
        return;
      }

      const resultB = await runCategoryGeneration("B", apartments);
      const resultC = await runCategoryGeneration("C", apartments);

      setGenerationResult({
        B: resultB,
        C: resultC,
      });
    } catch (error) {
      setActivityError(error.message || "Failed to generate invoices.");
    } finally {
      setIsGeneratingBoth(false);
    }
  };

  const previewMapping = async () => {
    try {
      ensureApiAndToken();
      ensureScope();

      if (!csvFile) {
        throw new Error("Select the recovery CSV file first.");
      }

      setActivityError("");
      setPreviewResult(null);
      setApplyResult(null);
      setSelectedManualTargets({});
      setIsPreviewing(true);

      const formData = new FormData();
      formData.append("file", csvFile);
      formData.append("target_month", config.month);
      formData.append("target_year", config.year);
      formData.append("cutoff", config.cutoff);

      if (config.landlordId) {
        formData.append("landlord_id", config.landlordId);
      } else {
        formData.append("apartment_id", config.apartmentId);
      }

      const response = await fetch(`${config.apiBaseUrl}/tenant/invoice-recovery-preview/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        body: formData,
      });

      const data = await handleJsonResponse(response);
      setPreviewResult(data);
    } catch (error) {
      setActivityError(error.message || "Failed to preview recovery mapping.");
    } finally {
      setIsPreviewing(false);
    }
  };

  const summary = previewResult?.summary || {};
  const readyRows = previewResult?.ready || [];
  const missingRows = previewResult?.target_missing || [];
  const manualReviewRows = previewResult?.manual_review || [];
  const negativeRows = previewResult?.negative_rows || [];
  const selectedManualItems = Object.values(selectedManualTargets).filter(Boolean);
  const totalApplyItems = [...readyRows, ...selectedManualItems];
  const generationRows = [
    ...(generationResult?.B?.results || []).map((row) => ({ ...row, category: "B" })),
    ...(generationResult?.C?.results || []).map((row) => ({ ...row, category: "C" })),
  ];

  const buildManualSelection = (row, invoice) => ({
    csv_invoice_number: row.csv_invoice_number,
    csv_cur_receipt_number: row.csv_cur_receipt_number,
    csv_total_receipt_number: row.csv_total_receipt_number,
    csv_internal_data: row.csv_internal_data,
    csv_receipt_signature: row.csv_receipt_signature,
    csv_qr_code: row.csv_qr_code,
    csv_created_at: row.csv_created_at || row.created_at,
    client_name: row.client_name,
    client_pin: row.client_pin,
    amount: row.amount,
    category: row.category || invoice.category,
    template_invoice: row.template_invoice || null,
    target_month: invoice.month,
    target_year: invoice.year,
    target_invoice: invoice,
    existing_kra_invoice: invoice.existing_kra_invoice || null,
    action: invoice.existing_kra_invoice ? "replace_existing_kra" : "create_kra",
  });

  const openManualReviewModal = (row, rowIndex) => {
    const candidateTargets = (row.candidate_targets || []).filter((candidate) => candidate.target_invoice);
    const templateInvoiceId = row.candidate_templates?.[0]?.invoice_id ? String(row.candidate_templates[0].invoice_id) : "";
    const selectedInvoice = selectedManualTargets[rowIndex]?.target_invoice || null;

    setManualModalState({
      isOpen: true,
      rowIndex,
      row,
      searchQuery: row.client_name || "",
      searchMonth: config.month,
      searchYear: config.year,
      templateInvoiceId,
      searchResults: candidateTargets.map((candidate) => ({ ...candidate.target_invoice, existing_kra_invoice: candidate.existing_kra_invoice })),
      isSearching: false,
      isCreating: false,
      isBackdating: false,
      backdatedCreatedAt: toDateTimeLocalValue(row.csv_created_at || row.created_at),
      error: "",
      message: "",
      selectedInvoice,
    });
  };

  const closeManualReviewModal = () => {
    setManualModalState((current) => ({ ...current, isOpen: false, row: null, rowIndex: null }));
  };

  const clearManualModalSelection = () => {
    setManualModalState((current) => ({ ...current, selectedInvoice: null, message: "Selection cleared." }));
    if (manualModalState.rowIndex !== null) {
      setSelectedManualTargets((current) => {
        const next = { ...current };
        delete next[manualModalState.rowIndex];
        return next;
      });
    }
  };

  const searchInvoicesForModal = async () => {
    try {
      ensureApiAndToken();
      ensureScope();
      setManualModalState((current) => ({ ...current, isSearching: true, error: "", message: "" }));

      const response = await fetch(`${config.apiBaseUrl}/tenant/invoice-recovery-search/`, {
        method: "POST",
        headers: buildJsonHeaders(),
        body: JSON.stringify({
          landlord_id: config.landlordId || undefined,
          apartment_id: config.apartmentId || undefined,
          query: manualModalState.searchQuery,
          month: manualModalState.searchMonth,
          year: manualModalState.searchYear,
          template_invoice_id: manualModalState.templateInvoiceId || undefined,
        }),
      });

      const data = await handleJsonResponse(response);
      setManualModalState((current) => ({
        ...current,
        isSearching: false,
        searchResults: data.results || [],
        message: `${data.count || 0} invoice(s) found.`,
      }));
    } catch (error) {
      setManualModalState((current) => ({ ...current, isSearching: false, error: error.message || "Search failed." }));
    }
  };

  const createInvoiceFromModal = async () => {
    try {
      ensureApiAndToken();
      if (!manualModalState.templateInvoiceId) {
        throw new Error("Select a template invoice first.");
      }

      setManualModalState((current) => ({ ...current, isCreating: true, error: "", message: "" }));

      const response = await fetch(`${config.apiBaseUrl}/tenant/invoice-recovery-create-invoice/`, {
        method: "POST",
        headers: buildJsonHeaders(),
        body: JSON.stringify({
          template_invoice_id: manualModalState.templateInvoiceId,
          month: manualModalState.searchMonth,
          year: manualModalState.searchYear,
          backdated_created_at: manualModalState.backdatedCreatedAt || undefined,
        }),
      });

      const data = await handleJsonResponse(response);
      const invoice = data.invoice;

      setManualModalState((current) => ({
        ...current,
        isCreating: false,
        searchResults: invoice
          ? [invoice, ...current.searchResults.filter((item) => item.invoice_id !== invoice.invoice_id)]
          : current.searchResults,
        selectedInvoice: invoice || current.selectedInvoice,
        message: data.created ? `Invoice ${invoice?.invoice_id} created.` : `Invoice ${invoice?.invoice_id} already existed.`,
      }));

      if (invoice && manualModalState.row) {
        setSelectedManualTargets((current) => ({
          ...current,
          [manualModalState.rowIndex]: buildManualSelection(manualModalState.row, invoice),
        }));
      }
    } catch (error) {
      setManualModalState((current) => ({ ...current, isCreating: false, error: error.message || "Failed to create invoice." }));
    }
  };

  const backdateInvoiceFromModal = async () => {
    try {
      ensureApiAndToken();

      if (!manualModalState.selectedInvoice?.invoice_id) {
        throw new Error("Select an invoice first.");
      }
      if (!manualModalState.backdatedCreatedAt) {
        throw new Error("Enter the backdated created-at value first.");
      }

      setManualModalState((current) => ({ ...current, isBackdating: true, error: "", message: "" }));

      const response = await fetch(`${config.apiBaseUrl}/tenant/invoice-recovery-backdate-invoice/`, {
        method: "POST",
        headers: buildJsonHeaders(),
        body: JSON.stringify({
          invoice_id: manualModalState.selectedInvoice.invoice_id,
          created_at: manualModalState.backdatedCreatedAt,
        }),
      });

      const data = await handleJsonResponse(response);
      const invoice = data.invoice;

      setManualModalState((current) => ({
        ...current,
        isBackdating: false,
        searchResults: invoice
          ? current.searchResults.map((item) => (item.invoice_id === invoice.invoice_id ? invoice : item))
          : current.searchResults,
        selectedInvoice: invoice || current.selectedInvoice,
        message: invoice
          ? `Invoice ${invoice.invoice_id} backdated to ${invoice.created_at || manualModalState.backdatedCreatedAt}.`
          : "Invoice backdated.",
      }));

      if (invoice && manualModalState.row) {
        setSelectedManualTargets((current) => ({
          ...current,
          [manualModalState.rowIndex]: buildManualSelection(manualModalState.row, invoice),
        }));
      }
    } catch (error) {
      setManualModalState((current) => ({ ...current, isBackdating: false, error: error.message || "Failed to backdate invoice." }));
    }
  };

  const useInvoiceForManualRow = (invoice, closeAfter = false) => {
    if (!manualModalState.row || !invoice) {
      return;
    }

    setSelectedManualTargets((current) => ({
      ...current,
      [manualModalState.rowIndex]: buildManualSelection(manualModalState.row, invoice),
    }));
    setManualModalState((current) => ({
      ...current,
      selectedInvoice: invoice,
      message: `Mapped to invoice ${invoice.invoice_id}.`,
      error: "",
      isOpen: closeAfter ? false : current.isOpen,
      row: closeAfter ? null : current.row,
      rowIndex: closeAfter ? null : current.rowIndex,
    }));
  };

  const applyMapping = async () => {
    try {
      ensureApiAndToken();

      if (!totalApplyItems.length) {
        throw new Error("Preview the mapping first or choose at least one manual review target.");
      }

      const confirmed = window.confirm(
        `Apply ${totalApplyItems.length} mapped KRA rows for ${scopeLabel} and ${config.replaceExisting ? "replace" : "keep"} existing KRA records where applicable?`
      );

      if (!confirmed) {
        return;
      }

      setActivityError("");
      setIsApplying(true);

      const response = await fetch(`${config.apiBaseUrl}/tenant/invoice-recovery-apply/`, {
        method: "POST",
        headers: buildJsonHeaders(),
        body: JSON.stringify({
          items: totalApplyItems,
          replace_existing: config.replaceExisting,
        }),
      });

      const data = await handleJsonResponse(response);
      setApplyResult(data);
    } catch (error) {
      setActivityError(error.message || "Failed to apply recovery mapping.");
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <div className="w-full px-4 py-4">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="rounded-3xl border border-emerald-200 bg-[linear-gradient(135deg,#ecfdf5_0%,#ffffff_55%,#eff6ff_100%)] px-6 py-6 shadow-sm">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-800">
            Invoice Recovery Console
          </p>
          <h1 className="mt-2 text-3xl font-semibold text-slate-900">
            Recover invoices for all apartments under a landlord
          </h1>
          <p className="mt-3 max-w-4xl text-sm leading-6 text-slate-700">
            This screen is isolated from the normal invoicing pages. For landlord-driven recovery,
            leave Apartment ID blank, enter the landlord ID, generate the missing monthly invoices
            across that landlord's apartments, then preview and apply the post-cutoff CSV mapping.
          </p>
        </div>

        {activityError ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-800">
            {activityError}
          </div>
        ) : null}

        <div className="rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Recovery Inputs</h2>
              <p className="mt-2 text-sm text-slate-600">
                Landlord scope takes priority over apartment scope. For your case, keep landlord ID
                set to <span className="font-semibold text-slate-900">102</span>.
              </p>
            </div>
            <label className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <input
                type="checkbox"
                checked={config.replaceExisting}
                onChange={updateField("replaceExisting")}
                className="h-4 w-4"
              />
              Replace existing KRA rows when applying recovery
            </label>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <InputField
              label="API Base URL"
              value={config.apiBaseUrl}
              onChange={updateField("apiBaseUrl")}
              placeholder="http://127.0.0.1:8002/api"
            />
            <InputField
              label="Bearer Token"
              value={config.bearerToken}
              onChange={updateField("bearerToken")}
              placeholder="Paste access token"
            />
            <InputField
              label="Landlord ID"
              value={config.landlordId}
              onChange={updateField("landlordId")}
              placeholder="102"
            />
            <InputField
              label="Apartment ID"
              value={config.apartmentId}
              onChange={updateField("apartmentId")}
              placeholder="Optional single-apartment override"
            />
            <InputField
              label="Company ID"
              value={config.companyId}
              onChange={updateField("companyId")}
              placeholder="KRA company credentials ID"
            />
            <InputField
              label="Target Month"
              value={config.month}
              onChange={updateField("month")}
              placeholder="february"
            />
            <InputField
              label="Target Year"
              value={config.year}
              onChange={updateField("year")}
              placeholder="2026"
            />
            <InputField
              label="CSV Cutoff"
              value={config.cutoff}
              onChange={updateField("cutoff")}
              placeholder="2026-02-26T00:00"
              type="datetime-local"
            />
            <InputField
              label="Generation Created At"
              value={config.generationBackdatedCreatedAt}
              onChange={updateField("generationBackdatedCreatedAt")}
              placeholder="2026-02-27T09:00"
              type="datetime-local"
            />
            <label className="block md:col-span-2 xl:col-span-2">
              <span className="mb-2 block text-sm font-medium text-slate-700">Recovery CSV</span>
              <input
                type="file"
                accept=".csv"
                onChange={(event) => setCsvFile(event.target.files?.[0] || null)}
                className="block w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 file:mr-4 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium"
              />
            </label>
          </div>

          <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
            Current scope: <span className="font-semibold">{scopeLabel}</span>
          </div>

          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            If `Generation Created At` is set, any new `TenantInvoice` rows created by the
            generation buttons on this page will be backdated to that timestamp. Existing
            invoices found by the generator are not changed.
          </div>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Recovery Actions
            </p>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              <ActionButton
                label={isLoadingApartments ? "Loading apartments..." : "Load Landlord Apartments"}
                onClick={() => {
                  fetchLandlordApartments(true).catch((error) =>
                    setActivityError(error.message || "Failed to load landlord apartments.")
                  );
                }}
                disabled={isLoadingApartments || !config.landlordId}
                tone="ghost"
              />
              <ActionButton
                label={isGeneratingB ? "Generating B..." : "Generate Category B"}
                onClick={() => generateInvoices("B")}
                disabled={isGeneratingB || isGeneratingBoth}
                tone="slate"
              />
              <ActionButton
                label={isGeneratingC ? "Generating C..." : "Generate Category C"}
                onClick={() => generateInvoices("C")}
                disabled={isGeneratingC || isGeneratingBoth}
                tone="sky"
              />
              <ActionButton
                label={isGeneratingBoth ? "Generating B and C..." : "Generate B Then C"}
                onClick={generateBoth}
                disabled={isGeneratingBoth || isGeneratingB || isGeneratingC}
                tone="emerald"
              />
              <ActionButton
                label={isPreviewing ? "Previewing CSV Mapping..." : "Preview CSV Mapping"}
                onClick={previewMapping}
                disabled={isPreviewing}
                tone="emerald"
              />
              <ActionButton
                label={isApplying ? "Applying Mapped Rows..." : `Apply Mapped Rows${totalApplyItems.length ? ` (${totalApplyItems.length})` : ""}`}
                onClick={applyMapping}
                disabled={isApplying || !totalApplyItems.length}
                tone="amber"
              />
            </div>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
          <SummaryCard label="Loaded Apartments" value={landlordApartments.length} tone="sky" />
          <SummaryCard label="Scope Apartments" value={summary.apartment_scope_count || 0} tone="sky" />
          <SummaryCard label="Post-Cutoff Rows" value={summary.post_cutoff_rows || 0} />
          <SummaryCard label="Ready To Apply" value={summary.ready_rows || 0} tone="emerald" />
          <SummaryCard
            label="Manual Review"
            value={summary.manual_review_rows || 0}
            tone={manualReviewRows.length ? "rose" : "slate"}
          />
          <SummaryCard
            label="Selected Manual"
            value={selectedManualItems.length}
            tone={selectedManualItems.length ? "amber" : "slate"}
          />
        </div>

        <div className="grid gap-6 xl:grid-cols-2">
          <DataTable
            title="Landlord Apartments"
            rows={landlordApartments}
            emptyMessage="Load apartments for the landlord to confirm the generation scope."
            columns={[
              { key: "id", label: "Apartment ID" },
              { key: "name", label: "Apartment" },
              { key: "location", label: "Location" },
              { key: "num_houses", label: "Units" },
              { key: "occupancy", label: "Occupancy %" },
            ]}
          />
          <DataTable
            title="Generation Results"
            rows={generationRows}
            emptyMessage="Generate category B and/or C to see apartment-by-apartment results."
            columns={[
              { key: "category", label: "Category" },
              { key: "apartment_id", label: "Apartment ID" },
              { key: "apartment_name", label: "Apartment" },
              { key: "status", label: "Status" },
              {
                key: "message",
                label: "Details",
                render: (row) => row.error || row.response?.message || row.response?.detail || "Completed",
              },
            ]}
          />
        </div>

        <div className="grid gap-6 xl:grid-cols-2">
          <JsonPanel
            title="Generation Responses"
            data={generationResult}
            emptyMessage="Run category B and/or C generation to see the backend response."
          />
          <JsonPanel
            title="Apply Response"
            data={applyResult}
            emptyMessage="Apply the previewed rows to create or replace the KRA records."
          />
        </div>

        <DataTable
          title="Ready Mappings"
          rows={readyRows}
          emptyMessage="Preview the CSV to see the rows that can be applied automatically."
          columns={[
            { key: "csv_invoice_number", label: "CSV KRA Invoice" },
            { key: "client_name", label: "Client" },
            { key: "client_pin", label: "PIN" },
            { key: "amount", label: "Amount" },
            { key: "category", label: "Category" },
            {
              key: "target_invoice",
              label: "Target Invoice",
              render: (row) =>
                row.target_invoice
                  ? `${row.target_invoice.invoice_id} | ${row.target_invoice.house_number || "-"} | ${
                      row.target_invoice.user_name || "-"
                    }`
                  : "-",
            },
            {
              key: "existing_kra_invoice",
              label: "Current KRA",
              render: (row) =>
                row.existing_kra_invoice?.kra_invoice_number
                  ? `${row.existing_kra_invoice.kra_invoice_number}`
                  : "None",
            },
            { key: "action", label: "Action" },
          ]}
        />

        <div className="grid gap-6 xl:grid-cols-2">
          <DataTable
            title="Missing Target Invoices"
            rows={missingRows}
            emptyMessage="All previewed rows found a target invoice."
            columns={[
              { key: "csv_invoice_number", label: "CSV KRA Invoice" },
              { key: "client_name", label: "Client" },
              { key: "client_pin", label: "PIN" },
              { key: "amount", label: "Amount" },
              { key: "category", label: "Category" },
              { key: "target_month", label: "Target Month" },
              { key: "target_year", label: "Target Year" },
              { key: "reason", label: "Reason" },
            ]}
          />

          <DataTable
            title="Negative CSV Rows"
            rows={negativeRows}
            emptyMessage="No negative post-cutoff rows were found in the preview."
            columns={[
              { key: "csv_invoice_number", label: "CSV KRA Invoice" },
              { key: "client_name", label: "Client" },
              { key: "client_pin", label: "PIN" },
              { key: "amount", label: "Amount" },
              { key: "created_at", label: "Created At" },
            ]}
          />
        </div>
        <DataTable
          title="Manual Review"
          rows={manualReviewRows}
          emptyMessage="No manual review rows were found in the preview."
          columns={[
            { key: "csv_invoice_number", label: "CSV KRA Invoice" },
            { key: "client_name", label: "Client" },
            { key: "client_pin", label: "PIN" },
            { key: "amount", label: "Amount" },
            { key: "reason", label: "Reason" },
            {
              key: "selected_target",
              label: "Selected Target",
              render: (_, index) => {
                const selected = selectedManualTargets[index]?.target_invoice;
                if (!selected) {
                  return "-";
                }
                return `${selected.invoice_id} | ${selected.house_number || "-"} | ${selected.user_name || "-"} | existing KRA ${selected.existing_kra_invoice?.kra_invoice_number || "None"}`;
              },
            },
            {
              key: "review",
              label: "Review",
              render: (row, index) => (
                <ActionButton
                  label="Open Manual Review"
                  onClick={() => openManualReviewModal(row, index)}
                  disabled={false}
                  tone="ghost"
                />
              ),
            },
          ]}
        />
      </div>

      <ManualReviewModal
        isOpen={manualModalState.isOpen}
        row={manualModalState.row}
        modalState={manualModalState}
        setModalState={setManualModalState}
        onClose={closeManualReviewModal}
        onSearch={searchInvoicesForModal}
        onCreateInvoice={createInvoiceFromModal}
        onBackdateInvoice={backdateInvoiceFromModal}
        onUseInvoice={useInvoiceForManualRow}
        onClearSelection={clearManualModalSelection}
      />
    </div>
  );
}
